import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Paper,
  Slide,
  TextField,
  Typography,
} from '@material-ui/core';
import React, { useState, useRef } from 'react';
import { ConfirmDeliveryParams } from 'src/interfaces/IInventoryOrder';
import { useConfirmDelivery } from 'src/hooks/queries/useInventoryOrders';
import { Close } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { RootState, dispatch } from 'src/rematch';
import { usePractitionerInfo } from 'src/hooks/queries/usePractitioners';
import { formatCurrency } from 'src/utils/formatNumber';
import { USER_TYPES } from 'src/constants/general.constants';
import { useMedspaPractitioners } from 'src/hooks/queries/useMedspaAdmins';
import {
  EXPIRATION_DATE_REQUIRED_TEXT,
  EXPIRATION_DATE_WARNING_TEXT,
  INPUT_DATE_FORMAT,
} from 'src/constants/inventory.constants';
import moment from 'moment';
import DateComponent from 'src/components/common/DateComponent';
import { useStyle } from '../inventoryOrder.styles';

const ConfirmDelivery: React.FC<ConfirmDeliveryParams> = ({
  open,
  toggleView,
  quantity,
  supplierName,
  startingUnits,
  unitPrice,
  orderMultiplier,
  purchaseOrderItemId,
  name,
}) => {
  const classes = useStyle();

  const [lot, setLot] = useState<string | undefined>();
  const [expireAt, setExpireAt] = useState<string | undefined>();
  const [formChanged, setFormChanged] = useState<boolean>(false);

  const lotInputRef = useRef<HTMLFormElement | null>(null);

  const { mutateAsync: confirmDelivery } = useConfirmDelivery(purchaseOrderItemId);
  const { userId, userType } = useSelector(({ auth }: RootState) => auth);

  const isMedspaAdmin = userType !== USER_TYPES.PRACTITIONER;
  const { data: medspaPractitioners } = useMedspaPractitioners(isMedspaAdmin);
  const practitionerId = isMedspaAdmin ? (medspaPractitioners ?? [])[0]?.id ?? 0 : userId;

  const {
    practitionerInfo: { signatureImageUrl },
  } = usePractitionerInfo(practitionerId ?? 0);

  const expireDateGreaterThanAYear = moment(expireAt).isAfter(moment().add(1, 'year'));
  const expireDateGreaterThanNow = moment(expireAt).isAfter(moment());
  const enableSubmit = !!lot && !!expireAt && expireDateGreaterThanNow;

  const resetForm = () => {
    setLot(undefined);
    setExpireAt(undefined);
    lotInputRef?.current?.reset();
    setFormChanged(false);
  };

  const handleChangeDate = (date: string | Date) => {
    setExpireAt(moment(date).format(INPUT_DATE_FORMAT));
    setFormChanged(true);
  };

  const handleChangeLot = (event: React.ChangeEvent) => {
    setLot((event.currentTarget as HTMLInputElement)?.value);
    setFormChanged(true);
  };

  const handleSave = async (): Promise<void> => {
    if (enableSubmit) {
      await confirmDelivery({ lot, expireAt });

      !isMedspaAdmin &&
        dispatch({
          type: 'inventoryDefault/getDashboardLists',
          payload: practitionerId,
        });

      resetForm();
      toggleView();
    }
  };

  return (
    <Slide direction="left" enter in={open} exit timeout={{ enter: 500, exit: 100 }}>
      <Paper className={classes.editInsert} variant="elevation" elevation={2}>
        <Box className={classes.confirmTitle}>
          <Typography variant="h4">Confirm your order</Typography>
          <IconButton
            className={classes.closeButton}
            size="small"
            onClick={() => {
              resetForm();
              toggleView();
            }}
            data-testid="confirmDelivery.close"
          >
            <Close />
          </IconButton>
        </Box>

        <Box className={classes.productDetail}>
          <Box>
            <Typography>Product Name</Typography>
            <Typography>Boxes</Typography>
            <Typography>Quantity</Typography>
            <Typography>Total</Typography>
          </Box>
          <Box>
            <Typography>
              {name} ({supplierName})
            </Typography>
            <Typography>{Math.floor(quantity / (orderMultiplier || 1))}</Typography>
            <Typography>{quantity}</Typography>
            <Typography>
              {formatCurrency((startingUnits || 1) * (unitPrice ?? 0) * (orderMultiplier || 1) * quantity)}
            </Typography>
          </Box>
        </Box>
        <Box paddingTop="20px !important">
          <FormControl component="form" ref={lotInputRef} className={classes.confirmQtyInput}>
            <TextField
              value={lot}
              label="Lot #"
              className={classes.lotTextInput}
              onChange={handleChangeLot}
              name="lot"
              fullWidth
              data-testid="confirmDelivery.lot"
              InputLabelProps={{
                shrink: true,
              }}
              style={{ paddingTop: '5px' }}
            />
          </FormControl>
          <Box className={classes.lotInput} width="90%" paddingLeft="10%" minHeight="101px">
            <InputLabel shrink htmlFor="expireAt">
              Expiration Date
            </InputLabel>
            <DateComponent
              isBack={false}
              currentDate={expireAt && new Date(expireAt)}
              getDateValue={handleChangeDate}
            />
            <Box minHeight="86px" display="flex" alignItems="center" margin="0">
              {formChanged && !!expireAt && !expireDateGreaterThanAYear && (
                <Typography className={classes.expireDateError}>{EXPIRATION_DATE_WARNING_TEXT}</Typography>
              )}
              {formChanged && !expireAt && (
                <Typography className={classes.expireDateError}>{EXPIRATION_DATE_REQUIRED_TEXT}</Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Typography className={classes.signAndConfirm}>
          Please sign and confirm all items are entered correctly and the package was received
        </Typography>
        {signatureImageUrl && (
          <img
            alt="signature"
            src={signatureImageUrl}
            className={classes.signature}
            data-testid="confirmDelivery.signature"
          />
        )}
        <Button
          disabled={!enableSubmit}
          variant="outlined"
          onClick={handleSave}
          className={classes.saveDelivery}
          data-testid="confirmDelivery.save"
        >
          I confirm I received this item
        </Button>
      </Paper>
    </Slide>
  );
};

export { ConfirmDelivery, ConfirmDelivery as default };
